<template>
  <VApp id="app">
    <VCard
      v-scroll.self="onScroll"
      class="p-0 shadow-none app-container d-flex flex-column overflow-y-auto"
    >
      <SSpinner
        :isGlobalLoading="isGlobalLoading"
        class="spinner-wrapper"
        size="70"
        :label="getLabel"
      />
      <SGlobalErrorDialog
        ref="globalErrorDialog"
        @trackGtmEventServerErrorOccurred="onTrackEventServerErrorOccurred"
      />
      <SessionExpiredDialog />
      <SNightCardInviteDialog
        v-if="isNightCardsSupport"
        v-model="showNightCardInvite"
        :info-link-url="nightCardProgramInfoUrl"
        @later="onNightCardInviteLater"
        @go-to-settings="goToNightCardSettings"
      />
      <SNightCardExpirationReminderDialog
        v-if="isNightCardsSupport"
        v-model="showNightCardExpirationReminder"
        :expiration-at="getLinkedNrCardExpirationAt"
        @dismiss="onNightCardExpirationReminderDismiss"
        @go-to-settings="goToNightCardSettings"
      />
      <SNightCardLinkErrorSnackbar v-if="isNightCardsSupport" />
      <div
        id="main-container"
        class="main-container"
      >
        <TheHeader :isHeaderShown="isHeaderShown" />
        <VMain
          id="main-content"
          tabindex="-1"
          class="pt-0 page-content-container"
          :class="!isCurrentPageCheckout && 'mt-13'"
          :aria-label="$t('accessibility.mainContent')"
        >
          <RouterView class="h-100 pageBg page-content-container" />
        </VMain>
        <Footer />
      </div>
    </VCard>
    <CookieManagement />
  </VApp>
</template>

<script lang="ts">
export default {
  name: 'App',
  metaInfo: {
    // todo add dynamic for each SPA page -> R3
    title: 'NightRider',
  },
};
</script>

<script setup lang="ts">
import {
  computed, nextTick, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import appConfig from '@/appConfig';
import resolveNightCardProgramInfoUrl from '@/utils/resolveNightCardProgramInfoUrl';
import {
  SSpinner, SGlobalErrorDialog, SNightCardInviteDialog, SNightCardExpirationReminderDialog,
  SNightCardLinkErrorSnackbar, SUserService,
} from '@slg/web-customer-shared';
import { shouldShowNightCardExpirationReminder } from '@slg/web-customer-shared/src/utils/nightCardExpirationLogic';
import TheHeader from '@/components/theHeader/TheHeader.vue';
import Footer from '@/components/footer/Footer.vue';
import SessionExpiredDialog from '@/components/common/SessionExpiredDialog.vue';
import CookieManagement from '@/components/cookie/CookieManagement.vue';
import {
  useStore, useRouter, useRouteRef, useI18n, useVm, useGtm,
} from '@/composables';

const store = useStore();
const router = useRouter();
const route = useRouteRef();
const { i18n } = useI18n();
const vm = useVm();
const { trackGtmEvent } = useGtm();

const isHeaderShown = ref(true);
const lastScrollPosition = ref(0);
const showNightCardInvite = ref(false);
const showNightCardExpirationReminder = ref(false);
const isNightCardProfileReady = ref(false);
const globalErrorDialog = ref<{ open?: Function } | null>(null);

const isGlobalLoading = computed(() => store.getters['common/isGlobalLoading']);
const getLocation = computed(() => store.getters['auth/getLocation']);
const getLabel = computed(() => store.getters['common/getLabel']);
const getAppConfig = computed(() => store.getters['common/getAppConfig']);
const getIsAuth = computed(() => store.getters['auth/getIsAuth']);
const getNightRiderCardNumber = computed(() => store.getters['auth/getNightRiderCardNumber']);
const getLinkedNrCardExpirationAt = computed(() => store.getters['auth/getLinkedNrCardExpirationAt']);
const getDismissExpirationReminder = computed(() => store.getters['auth/getDismissExpirationReminder']);
const isCurrentPageCheckout = computed(() => route.value.name === 'Checkout');
const isNightCardsSupport = computed(() => !!(getAppConfig.value?.isNightCardsSupport ?? appConfig.isNightCardsSupport));
const nightCardProgramInfoUrl = computed(() => resolveNightCardProgramInfoUrl(
  appConfig.nightCardProgramInfoUrl,
  i18n.locale,
  appConfig,
));

function syncNightCardDialogsVisibility() {
  if (!isNightCardsSupport.value || !getIsAuth.value) {
    showNightCardInvite.value = false;
    showNightCardExpirationReminder.value = false;
    if (!getIsAuth.value) {
      isNightCardProfileReady.value = false;
    }
    return;
  }

  // Wait until profile is loaded so we don't flash "Add Night Card"
  // for customers who already have a linked card.
  if (!isNightCardProfileReady.value) {
    showNightCardInvite.value = false;
    showNightCardExpirationReminder.value = false;
    return;
  }

  if (!getNightRiderCardNumber.value) {
    showNightCardExpirationReminder.value = false;
    if (sessionStorage.getItem('nightCardInviteDismissed')) {
      showNightCardInvite.value = false;
      return;
    }
    showNightCardInvite.value = true;
    return;
  }

  showNightCardInvite.value = false;
  showNightCardExpirationReminder.value = shouldShowNightCardExpirationReminder({
    nightRiderCardNumber: getNightRiderCardNumber.value,
    linkedNrCardExpirationAt: getLinkedNrCardExpirationAt.value,
    dismissExpirationReminder: getDismissExpirationReminder.value,
  });
}

function onAuthSuccess() {
  sessionStorage.removeItem('nightCardInviteDismissed');
  isNightCardProfileReady.value = true;
  nextTick(() => {
    syncNightCardDialogsVisibility();
  });
}

async function onNightCardExpirationReminderDismiss() {
  try {
    await SUserService.updateProfile({ dismissExpirationReminder: true });
    await store.dispatch('auth/populateUserProfileInfo');
  } catch (e) {
    // Keep dialog dismissed locally even if profile update fails.
  }
  showNightCardExpirationReminder.value = false;
}

function onNightCardInviteLater() {
  sessionStorage.setItem('nightCardInviteDismissed', '1');
}

function goToNightCardSettings() {
  const lang = route.value.params.lang || appConfig.defaultLanguage;
  sessionStorage.setItem('nightCardInviteDismissed', '1');
  router.push({
    name: 'NightCardSettings',
    params: { lang },
  }).catch(() => {});
}

async function checkLocation() {
  const location = localStorage.getItem('location');
  if (!location) {
    await store.dispatch('auth/setGeoData');
  }
}

async function checkAuth() {
  const token = localStorage.getItem('authToken');
  if (token) {
    store.commit('auth/setAuthToken', token);
    try {
      await store.dispatch('auth/populateUserProfileInfo');
      isNightCardProfileReady.value = true;
      syncNightCardDialogsVisibility();
    } catch (e) {
      isNightCardProfileReady.value = false;
      store.commit('auth/clearAuthTokenAndProfileInfo');
    }
  }
}

async function initPlacesApi() {
  // eslint-disable-next-line no-undef
  const key = config.VUE_APP_GOOGLE_KEY || process.env.VUE_APP_GOOGLE_KEY;
  if (!document.querySelector('[data-name="googlePlaceApi"]')) {
    const placeApiScript = document.createElement('script');
    placeApiScript.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&language=${route.value.params.lang}`;
    placeApiScript.dataset.name = 'googlePlaceApi';
    document.head.appendChild(placeApiScript);
  }
}

function onScroll(e: Event) {
  const defaultScrollValue = 60;
  const target = e.target as HTMLElement;
  if (target.scrollTop > defaultScrollValue && lastScrollPosition.value < target.scrollTop) {
    isHeaderShown.value = false;
  }
  if (lastScrollPosition.value > target.scrollTop) {
    isHeaderShown.value = true;
  }
  lastScrollPosition.value = target.scrollTop;
}

function onTrackEventServerErrorOccurred() {
  trackGtmEvent('User clicks OK in Server Error pop-up', 'Button', 'Click', 'Server Error Occurred');
}

watch(getIsAuth, syncNightCardDialogsVisibility);
watch(getNightRiderCardNumber, syncNightCardDialogsVisibility);
watch(getLinkedNrCardExpirationAt, syncNightCardDialogsVisibility);
watch(getDismissExpirationReminder, syncNightCardDialogsVisibility);
watch(() => route.value.params.lang, (lang) => {
  const supported = appConfig.languages.includes(lang as string);
  i18n.locale = supported ? (lang as string) : appConfig.defaultLanguage;
}, { immediate: true });

async function createdInit() {
  store.commit('common/setAppConfig', appConfig);
  await initPlacesApi();
  await checkAuth();
  await checkLocation();
  syncNightCardDialogsVisibility();
}
createdInit();

onMounted(() => {
  syncNightCardDialogsVisibility();
  vm.$root.$on('auth-success', onAuthSuccess);
  (vm.$root as any).openGlobalErrorDialog = (payload: unknown) => globalErrorDialog.value?.open?.(payload);
  if (process.env.NODE_ENV === 'production') {
    const googleAdsScript = document.createElement('script');
    googleAdsScript.setAttribute('src', 'https://www.googletagmanager.com/gtag/js?id=AW-10805665019');
    googleAdsScript.async = true;
    document.head.appendChild(googleAdsScript);
    const googleAdsConfigScript = document.createElement('script');
    // eslint-disable-next-line max-len
    googleAdsConfigScript.text = 'window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag("js", new Date()); gtag("config", "AW-10805665019"); ';
    document.head.appendChild(googleAdsConfigScript);
  }
});

onBeforeUnmount(() => {
  vm.$root.$off('auth-success', onAuthSuccess);
});
</script>

<style lang="scss">
.app-container {
  height: 100vh;

  .main-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .page-content-container {
      @media screen and (max-width: 1264px) {
        min-height: calc(100vh - 52px);
      }
    }
  }
}
</style>
